import React, { useState, useEffect } from 'react';
import { ArrowRight, MapPin, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.slice(0, 8));
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero section */}
      <section className="relative h-[360px] sm:h-[420px] md:h-[500px] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_market-replica-29/artifacts/b1qllqzk_couvertine%20banner.png"
          alt="NEOPLIAGE - Installation de couvertine aluminium"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">NEOPLIAGE</h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2">Marque de NEOMAT</p>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8">Spécialiste de la tôlerie et du pliage aluminium</p>
            <Link
              to="/produits"
              className="bg-[#E42313] text-white px-6 sm:px-8 py-3 sm:py-4 rounded hover:bg-[#C11A0F] transition-colors inline-flex items-center gap-2 font-semibold text-base sm:text-lg"
            >
              Découvrir nos produits
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Products section */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 sm:mb-4">Nos Produits</h2>
        <p className="text-center text-gray-600 mb-8 sm:mb-12 text-base sm:text-lg">Découvrez notre gamme de produits en aluminium de qualité</p>
        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Chargement des produits...</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
              {products.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
            <div className="text-center">
              <Link
                to="/produits"
                className="inline-flex items-center gap-2 text-[#E42313] font-semibold text-lg hover:underline"
              >
                Voir tous les produits
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </>
        )}
      </section>

      {/* About section */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-6 md:mb-8">Qui sommes-nous ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <img
                  src="https://customer-assets.emergentagent.com/job_market-replica-29/artifacts/mkg7et49_atelier.png"
                  alt="Atelier NEOPLIAGE"
                  className="rounded-lg shadow-lg w-full h-auto"
                />
              </div>
              <div>
                <p className="text-gray-700 mb-4 text-lg">
                  <strong>NEOPLIAGE</strong> est la marque dédiée au pliage aluminium de <strong>NEOMAT</strong>, entreprise spécialisée dans la fabrication de pièces en aluminium pour le bâtiment, située à <strong>Ambérieu-en-Bugey</strong> dans l'Ain.
                </p>
                <p className="text-gray-700 mb-4">
                  Nous proposons une gamme complète de <strong>couvertines aluminium (1 mm et 1,5 mm)</strong>, de <strong>couvertines acier (0,75 mm)</strong>, de cornières et de pliages sur mesure.
                </p>
                <p className="text-gray-700 mb-6">
                  Notre atelier équipé de machines numériques de dernière génération nous permet de fabriquer des pièces jusqu'à 3 mètres de longueur.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <MapPin className="w-5 h-5 text-[#E42313]" />
                    <span>23 Avenue de la Libération, 01500 Ambérieu-en-Bugey</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone className="w-5 h-5 text-[#E42313]" />
                    <span>06.37.06.03.16</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="w-5 h-5 text-[#E42313]" />
                    <span>contact@neopliage.fr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="bg-[#E42313] rounded-lg p-6 sm:p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">Une demande sur mesure&nbsp;?</h2>
          <p className="text-base sm:text-xl mb-6 sm:mb-8">Tablettes, coudières, développés spéciaux — nous fabriquons sur plan</p>
          <Link
            to="/devis"
            className="bg-white text-[#E42313] px-6 sm:px-8 py-3 sm:py-4 rounded hover:bg-gray-100 transition-colors inline-flex items-center gap-2 font-semibold text-base sm:text-lg"
          >
            Demander un devis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
